import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableSortLabel, TableContainer, Paper,
  CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Avatar, useTheme, useMediaQuery, TextField,
} from '@mui/material';
import { Refresh as RefreshIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { usePaperMode } from '../contexts/PaperModeContext';
import { parseOptionSymbol, parseSymbolFilter } from '../utils/tradeUtils';
import TradeDialog from '../components/TradeDialog';

// Convert an expiration string to ISO (YYYY-MM-DD) for the trade-entry form.
// Handles YYMMDD (parseOptionSymbol), ISO, and MM/DD/YYYY.
const toIsoDate = (exp) => {
  const s = String(exp || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{6}$/.test(s)) return `20${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4, 6)}`;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    const y = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  return '';
};

// Convert a history row into the alert shape TradeDialog understands —
// pre-fills symbol, editable legs, price, quantity, and the source account.
const orderToAlert = (o) => {
  const legs = [];
  let underlying = '';

  // Paper orders carry structured leg fields on _paperLegs — prefer them over
  // parsing instrument symbols.
  if (o._accountType === 'paper' && Array.isArray(o._paperLegs) && o._paperLegs.length) {
    for (const p of o._paperLegs) {
      const isEq = String(p.asset_type || '').toUpperCase() === 'EQUITY';
      if (isEq) {
        if (!underlying) underlying = p.symbol || p.underlying || '';
        legs.push({ assetType: 'EQUITY', symbol: p.symbol, instruction: p.instruction, quantity: p.quantity || 1 });
      } else {
        if (!underlying) underlying = p.underlying || p.symbol || '';
        legs.push({
          assetType: 'OPTION',
          instruction: p.instruction,
          putCall: p.put_call,
          strike: p.strike,
          expiration: toIsoDate(p.expiration),
          quantity: p.quantity || 1,
        });
      }
    }
    const otP = String(o.orderType || '').toUpperCase();
    return {
      symbol: underlying,
      strategyType: 'custom',
      legs,
      orderType: otP === 'MARKET' ? 'MARKET' : 'LIMIT',
      price: o.price != null ? Math.abs(o.price) : undefined,
      quantity: legs[0]?.quantity || 1,
      duration: o.duration || 'DAY',
      tradeAccountId: 'paper',
    };
  }

  for (const l of (o.orderLegCollection || [])) {
    const inst = l.instrument || {};
    if (inst.assetType === 'OPTION') {
      const info = parseOptionSymbol(inst.symbol || inst.rhDisplay || '');
      if (!underlying) underlying = inst.underlyingSymbol || info.underlying || '';
      legs.push({
        assetType: 'OPTION',
        instruction: l.instruction,
        putCall: info.isOption ? info.type : 'C',
        strike: info.isOption ? info.strike : null,
        expiration: info.isOption ? toIsoDate(info.expiration) : '',
        quantity: l.quantity || 1,
      });
    } else {
      const sym = inst.symbol || inst.underlyingSymbol || '';
      if (!underlying) underlying = sym;
      legs.push({
        assetType: 'EQUITY',
        symbol: sym,
        instruction: l.instruction,
        quantity: l.quantity || 1,
      });
    }
  }
  const ot = String(o.orderType || '').toUpperCase();
  return {
    symbol: underlying,
    strategyType: 'custom',
    legs,
    // NET_* types are recomputed at submit time; LIMIT carries the price.
    orderType: ot === 'MARKET' ? 'MARKET' : 'LIMIT',
    price: o.price != null ? Math.abs(o.price) : undefined,
    quantity: legs[0]?.quantity || o.quantity || 1,
    duration: o.duration || 'DAY',
    tradeAccountId: o._accountId,
  };
};

const BROKER_LOGOS = {
  robinhood: '/assets/robinhood_logo.png',
  'robinhood-api': '/assets/robinhood_logo.png',
  'robinhood-mcp': '/assets/robinhood_logo.png',
  schwab: '/assets/schwab_logo.jpg',
  tradier: '/assets/tradier-logo.jpg',
  pershing: '/assets/bnym_pershing.png',
  paper: null,
};

const BrokerLogo = ({ broker, size = 20 }) => {
  const type = String(broker || '').toLowerCase();
  const logoSrc = BROKER_LOGOS[type];
  if (logoSrc) {
    return (
      <Box component="img" src={logoSrc} alt={type} sx={{ width: size, height: size, objectFit: 'contain', display: 'block', borderRadius: '4px' }} />
    );
  }
  const configs = {
    robinhood: { color: '#00C805', initial: 'R' },
    'robinhood-mcp': { color: '#00C805', initial: 'R' },
    schwab: { color: '#0076CF', initial: 'S' },
    tradier: { color: '#54565A', initial: 'T' },
    paper: { color: '#888888', initial: 'P' },
  };
  const cfg = configs[type] || { color: '#888888', initial: (type[0] || '?').toUpperCase() };
  return (
    <Avatar sx={{ width: size, height: size, fontSize: `${size * 0.5}px`, fontWeight: 'bold', bgcolor: cfg.color, color: '#fff' }}>
      {cfg.initial}
    </Avatar>
  );
};

const AccountWithLogo = ({ name, broker }) => (
  <Tooltip title={`${broker || ''} ${name || ''}`.trim()} arrow>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <BrokerLogo broker={broker} size={20} />
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>{name || '-'}</Typography>
    </Box>
  </Tooltip>
);

// Detect option strategy type from an order's legs (mirrors StockSummary.js logic)
const classifyOrderLegs = (orderLegs) => {
  if (!orderLegs || orderLegs.length === 0) return '-';
  // Roll orders mix closing legs (old position) with opening legs (new
  // position) in a single order — classifying all legs together misfires
  // (e.g. BTC old put + STO new put looks like a debit spread). The opening
  // side is what you now hold, so label the roll by it.
  const instrU = (l) => String(l.instruction || '').toUpperCase();
  const openLegs = orderLegs.filter(l => /(_TO_OPEN|SELL_SHORT)$/.test(instrU(l)));
  const closeLegs = orderLegs.filter(l => /(_TO_CLOSE|_TO_COVER)$/.test(instrU(l)));
  if (openLegs.length && closeLegs.length) {
    return `Roll ${detectStrategyFromOrderLegs(openLegs)}`;
  }
  if (orderLegs.length === 1) {
    const leg = orderLegs[0];
    const sym = leg.instrument?.symbol || '';
    const info = parseOptionSymbol(sym);
    if (info.isOption) {
      const isLong = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'SELL_TO_CLOSE';
      return `${isLong ? 'Long' : 'Short'} ${info.type === 'C' ? 'Call' : 'Put'}`;
    }
    return 'Stock';
  }

  const parsed = orderLegs.map(l => {
    const info = parseOptionSymbol(l.instrument?.symbol || '');
    const isLong = l.instruction === 'BUY_TO_OPEN' || l.instruction === 'SELL_TO_CLOSE';
    return { ...info, isLong, qty: l.quantity, instruction: l.instruction };
  });

  const calls = parsed.filter(p => p.isOption && p.type === 'C').sort((a, b) => a.strike - b.strike);
  const puts = parsed.filter(p => p.isOption && p.type === 'P').sort((a, b) => a.strike - b.strike);

  if (calls.length === 2 && puts.length === 2) {
    const [lc, hc] = calls;
    const [lp, hp] = puts;
    if (!lc.isLong && hc.isLong && !hp.isLong && lp.isLong) return 'Short Iron Condor';
    if (lc.isLong && !hc.isLong && hp.isLong && !lp.isLong) return 'Long Iron Condor';
    return 'Iron Condor';
  }

  if (calls.length === 2 && puts.length === 0) {
    const [lower, higher] = calls;
    if (lower.isLong && !higher.isLong) return 'Call Debit Spread';
    if (!lower.isLong && higher.isLong) return 'Call Credit Spread';
    return 'Call Spread';
  }

  if (puts.length === 2 && calls.length === 0) {
    const [lower, higher] = puts;
    if (!lower.isLong && higher.isLong) return 'Put Debit Spread';
    if (lower.isLong && !higher.isLong) return 'Put Credit Spread';
    return 'Put Spread';
  }

  if (calls.length === 1 && puts.length === 1) {
    const call = calls[0];
    const put = puts[0];
    if (call.strike === put.strike) return call.isLong ? 'Long Straddle' : 'Short Straddle';
    return call.isLong ? 'Long Strangle' : 'Short Strangle';
  }

  if (calls.length === 1 && puts.length === 2) {
    const call = calls[0];
    const [lowerPut, higherPut] = puts;
    if (!call.isLong && lowerPut.isLong && !higherPut.isLong) return 'Short Call + Put Credit Spread';
    if (!call.isLong && !lowerPut.isLong && higherPut.isLong) return 'Short Call + Put Debit Spread';
    if (call.isLong && lowerPut.isLong && !higherPut.isLong) return 'Long Call + Put Credit Spread';
    if (call.isLong && !lowerPut.isLong && higherPut.isLong) return 'Long Call + Put Debit Spread';
    return '3-Leg Combo';
  }

  if (calls.length === 2 && puts.length === 1) {
    const [lowerCall, higherCall] = calls;
    const put = puts[0];
    if (!put.isLong && lowerCall.isLong && !higherCall.isLong) return 'Short Put + Call Credit Spread';
    if (!put.isLong && !lowerCall.isLong && higherCall.isLong) return 'Short Put + Call Debit Spread';
    if (put.isLong && lowerCall.isLong && !higherCall.isLong) return 'Long Put + Call Credit Spread';
    if (put.isLong && !lowerCall.isLong && higherCall.isLong) return 'Long Put + Call Debit Spread';
    return '3-Leg Combo';
  }

  return 'Multi-Leg';
};

// Futures-option orders ("/…_P|C<strike>:<venue>" legs) keep their structure
// label but get a Futures marker — including rolls ("Roll Futures Short Put").
const detectStrategyFromOrderLegs = (orderLegs) => {
  const label = classifyOrderLegs(orderLegs);
  const hasFutures = (orderLegs || []).some(l =>
    parseOptionSymbol(l.instrument?.symbol || '').isFuturesOption);
  if (!hasFutures || label === 'Stock' || label === '-') return label;
  return label.startsWith('Roll ') ? `Roll Futures ${label.slice(5)}` : `Futures ${label}`;
};

const getActionLabel = (instruction) => {
  if (!instruction) return '-';
  const i = String(instruction).toUpperCase();
  if (['BUY_TO_OPEN', 'SELL_TO_OPEN', 'SELL_SHORT', 'BUY_TO_OPEN_MARKET'].includes(i)) return 'Open';
  if (['BUY_TO_CLOSE', 'SELL_TO_CLOSE', 'BUY_TO_COVER', 'SELL'].includes(i)) return 'Close';
  return i;
};

// Alert source for a paper trade — prefers the stored source column (may be a
// named source like a webhook-token name or "DISCORD #channel"), falls back to
// the alert_id naming conventions for rows written before it existed.
const paperSourceOf = (t) => {
  const s = String(t?.source || '').trim();
  if (s) return s;
  const a = String(t?.alert_id || '');
  if (a.startsWith('discord-')) return 'DISCORD';
  if (a.startsWith('scanner-')) return 'SCANNER';
  if (a.startsWith('manual_') || t?.reason === 'MANUAL') return 'MANUAL';
  return 'WEBHOOK';
};

// ALL-CAPS generic sources display as title case; named sources keep their case.
const fmtSource = (s) => {
  const v = String(s || '');
  return v && v === v.toUpperCase() ? v.charAt(0) + v.slice(1).toLowerCase() : v;
};

const fmtInstruction = (i) =>
  i ? String(i).toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

// Cr = money in (sell legs / NET_CREDIT), Dr = money out (buy legs / NET_DEBIT).
const creditDebitTag = (orderType, instruction) => {
  const ot = String(orderType || '').toUpperCase();
  if (ot === 'NET_CREDIT') return 'Cr';
  if (ot === 'NET_DEBIT') return 'Dr';
  const i = String(instruction || '').toUpperCase();
  if (i.startsWith('SELL')) return 'Cr';
  if (i.startsWith('BUY')) return 'Dr';
  return null;
};

const fmtCur = (n, pm) => {
  if (pm) return '••••••';
  const x = Number(n) || 0;
  return x.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
};

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_COLORS = {
  FILLED: 'success', WORKING: 'info', PENDING_ACTIVATION: 'info',
  CANCELED: 'default', CANCELLED: 'default', REJECTED: 'error', FAILED: 'error',
  EXPIRED: 'warning', LIVE: 'info', PENDING: 'info', CONFIRMED: 'success',
  UNCONFIRMED: 'info', QUEUED: 'info', PARTIAL_FILL: 'warning',
};
const getStatusColor = (s) => STATUS_COLORS[String(s || '').toUpperCase()] || 'default';

// Per-leg fill price: brokers don't put a price on orderLegCollection legs —
// Schwab carries executions in orderActivityCollection.executionLegs
// ({legId, price, quantity}), Robinhood legs carry an executions array.
// Weighted-average the fills for the leg; index fallback when legIds absent.
const legFillPrice = (o, leg, idx) => {
  if (leg.price != null && leg.price !== '') return Number(leg.price);
  const legId = leg.legId != null ? String(leg.legId) : null;
  const execs = [];
  (o.orderActivityCollection || []).forEach(a =>
    (a.executionLegs || []).forEach(el => execs.push(el)));
  // Robinhood raw legs (MCP path) expose executions on the leg itself.
  const rawLegs = (o.raw?.legs || []);
  const rawLeg = rawLegs.find(l => legId && String(l.id) === legId) || rawLegs[idx];
  if (rawLeg) (rawLeg.executions || []).forEach(e => execs.push(e));
  let qty = 0, notional = 0;
  execs.forEach(el => {
    const match = legId != null && el.legId != null ? String(el.legId) === legId : true;
    if (match) {
      const q = Number(el.quantity) || 0;
      qty += q;
      notional += (Number(el.price) || 0) * q;
    }
  });
  if (qty > 0) return notional / qty;
  if (execs[idx]) return Number(execs[idx].price) || null;
  return null;
};

const fmtOption = (sym) => {
  if (!sym) return '';
  const c = sym.replace(/\s+/g, '');
  const m = c.match(/^([A-Z]+)(\d{6})([CP])(\d{8})$/);
  if (m) {
    const [, u, d, p, st] = m;
    return `${u} ${d.slice(0,2)}/${d.slice(2,4)}/${d.slice(4,6)} ${p==='C'?'Call':'Put'} $${(parseInt(st)/1000).toFixed(2)}`;
  }
  return sym;
};

// Underlying ticker for a multi-leg order's header row — prefer a direct field,
// else parse the root out of the first option leg's OCC symbol.
const underlyingOf = (o) => {
  const direct = o.symbol || o.underlying || o.chainSymbol || o.chain_symbol;
  if (direct) return String(direct).trim();
  const sym = (o.orderLegCollection || []).map(l => l.instrument?.symbol || '').find(Boolean) || '';
  const m = sym.replace(/\s+/g, '').match(/^([A-Z]+)\d{6}[CP]/);
  return m ? m[1] : sym;
};

const normalizeRhOrder = (o, accountName, accountId) => {
  const rhLegs = o.legs || [];
  const orderLegCollection = rhLegs.map(l => {
    const side = String(l.side || '').toUpperCase();
    const eff = String(l.position_effect || l.positionEffect || '').toUpperCase();
    let instr = 'BUY_TO_OPEN';
    if (eff === 'OPEN' && side === 'SELL') instr = 'SELL_TO_OPEN';
    else if (eff === 'CLOSE' && side === 'BUY') instr = 'BUY_TO_CLOSE';
    else if (eff === 'CLOSE' && side === 'SELL') instr = 'SELL_TO_CLOSE';
    const expRaw = l.expirationDate || l.expiration_date || '';
    const yy = expRaw.slice(2,4), mm = expRaw.slice(5,7), dd = expRaw.slice(8,10);
    const pc = (l.optionType || l.option_type || '').toLowerCase() === 'put' ? 'P' : 'C';
    const strikeStr = String(Math.round(parseFloat(l.strikePrice||l.strike_price||0)*1000)).padStart(8,'0');
    const sym = `${o.chain_symbol||o.chainSymbol||''}${yy}${mm}${dd}${pc}${strikeStr}`;
    return { instrument: { symbol: sym, assetType: 'OPTION' }, instruction: instr, quantity: Number(l.quantity||o.quantity||0), price: l.price ?? null };
  });
  return {
    ...o,
    orderId: o.orderId || o.id,
    orderLegCollection,
    enteredTime: o.enteredTime || o.created_at || '',
    orderType: o.type || o.orderType || '',
    price: o.price,
    status: String(o.status || '').toUpperCase(),
    _accountName: accountName,
    _accountId: String(accountId),
    _broker: 'Robinhood',
    _accountType: 'robinhood-mcp',
  };
};

// The robin_stocks order-history endpoint emits one row per leg. Group rows by
// the underlying Robinhood order id so multi-leg orders display as one order,
// and rebuild OCC symbols per leg so orders can be repeated.
const normalizeRhHistory = (rows, accountName, accountId) => {
  const groups = new Map();
  for (const o of rows) {
    const key = o.raw?.id || o.orderId;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(o);
  }
  const out = [];
  for (const [key, grp] of groups) {
    const first = grp[0];
    const legs = grp.map(g => {
      const isOpt = g.assetType === 'OPTION';
      const side = String(g.side || '').toUpperCase();
      const eff = String(g.positionEffect || '').toLowerCase();
      const instruction = isOpt
        ? `${side === 'BUY' ? 'BUY' : 'SELL'}_TO_${eff === 'close' ? 'CLOSE' : 'OPEN'}`
        : (side === 'SELL' ? 'SELL' : 'BUY');
      let occ = null;
      if (isOpt && g.expirationDate && g.strike != null) {
        const [y, m, d] = String(g.expirationDate).split('-');
        const pc = String(g.optionType || '').toLowerCase() === 'put' ? 'P' : 'C';
        occ = `${String(g.chainSymbol || '').toUpperCase().padEnd(6)}${String(y).slice(2)}${m}${d}${pc}${String(Math.round(g.strike * 1000)).padStart(8, '0')}`;
      }
      return {
        instrument: { symbol: occ || g.symbol, assetType: g.assetType, rhDisplay: g.symbol },
        instruction,
        quantity: g.quantity,
        ratioQuantity: g.legRatio || 1,
        // Server reconciles per-leg fill price against the net premium.
        price: g.price != null ? Number(g.price) : null,
      };
    });
    const direction = String(first.raw?.direction || '').toLowerCase();
    const orderType = legs.length > 1
      ? (direction === 'credit' ? 'NET_CREDIT' : 'NET_DEBIT')
      : String(first.type || 'MARKET').toUpperCase();
    out.push({
      orderId: key,
      orderLegCollection: legs,
      enteredTime: first.enteredTime,
      orderType,
      price: legs.length > 1 ? (first.raw?.price ?? first.price) : first.price,
      status: String(first.status || '').toUpperCase(),
      _accountName: accountName,
      _accountId: String(accountId),
      _broker: 'Robinhood',
      _accountType: 'robinhood',
    });
  }
  return out;
};

// Friendly labels for paper-trade close/open reasons recorded by the server
// (STOP_LOSS/TAKE_PROFIT/EXPIRED/etc). Unknown values (e.g. SKIPPED reason
// sentences) are shown as-is.
const REASON_INFO = {
  STOP_LOSS: { label: 'Stop-loss', color: 'error', title: 'Closed by stop-loss tier' },
  TAKE_PROFIT: { label: 'Target', color: 'success', title: 'Closed by take-profit tier' },
  EXPIRED: { label: 'Expired', color: 'warning', title: 'Settled at expiration intrinsic value' },
  AUTO_EXIT: { label: 'Auto-exit', color: 'info', title: 'Closed by the auto-exit monitor' },
  MANUAL: { label: 'Manual', color: 'default', title: 'Manual order' },
  WEBHOOK_SIGNAL: { label: 'Alert', color: 'default', title: 'Opened by an alert signal' },
  WEBHOOK_CLOSE: { label: 'Alert close', color: 'default', title: 'Closed by an alert signal' },
};

const OrderHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { privacyMask } = usePrivacyMask();
  const { paperMode } = usePaperMode();
  const fc = (v) => fmtCur(v, privacyMask);
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selAcct, setSelAcct] = useState('all');
  const [statusFilter, setStatusFilter] = useState('filled');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [sort, setSort] = useState({ field: 'enteredTime', dir: 'desc' });
  const [editOrder, setEditOrder] = useState(null);   // alert-shaped order for TradeDialog
  const [updatedAt, setUpdatedAt] = useState(null);       // newest broker-cache timestamp seen
  const [copyByTargetId, setCopyByTargetId] = useState(new Map()); // copied order id -> event
  const [copyBySourceId, setCopyBySourceId] = useState(new Map()); // source order id -> event

  const fetchAccounts = useCallback(async () => {
    if (paperMode) { setAccounts([]); return; }
    try {
      let sA = [];
      try {
        const sR = await apiRequest('/api/schwab-api/accounts');
        sA = (sR.data?.accounts || []).map(a => ({ ...a, accountType: 'schwab' }));
      } catch (e) {}
      let rA = [];
      try {
        const rR = await apiRequest('/api/mcp/robinhood/accounts');
        rA = (rR.data?.accounts || []).map(a => ({ ...a, accountType: 'robinhood-mcp' }));
      } catch (e) {}
      // REST-backed accounts (api_type 'robinhood' / 'robinhood-api') come from
      // the main accounts list — the MCP accounts endpoint only covers 'robinhood-mcp'.
      let rhA = [];
      try {
        const aR = await apiRequest('/api/accounts');
        const all = aR.data?.accounts || (Array.isArray(aR.data) ? aR.data : []);
        rhA = all
          .filter(a => ['robinhood', 'robinhood-api'].includes(String(a.api_type || a.apiType || '').toLowerCase()))
          .map(a => ({ ...a, accountType: 'robinhood' }));
      } catch (e) {}
      setAccounts([...sA, ...rA, ...rhA]);
    } catch (e) { console.error('Accounts fetch failed:', e); }
  }, [paperMode]);

  const fetchPaperTrades = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiRequest('/api/paper-trading/trades?limit=500');
      const trades = r.data?.data || [];
      // Group legs written by one execution (e.g. a 2-leg spread) — they share
      // the same alert_id. Open and close fills on the same alert are separate
      // orders. Sentinel values written by the old exit monitor ('STOP_LOSS',
      // 'TAKE_PROFIT', ...) are reasons, not alert ids — never group on them.
      const SENTINEL_ALERT_IDS = new Set(['STOP_LOSS', 'TAKE_PROFIT', 'EXPIRED', 'AUTO_EXIT', 'MANUAL', 'WEBHOOK_CLOSE', 'WEBHOOK_SIGNAL']);
      const isCloseInstruction = (i) => /_TO_CLOSE$|^SELL$|^BUY_TO_COVER$/.test(String(i || '').toUpperCase());
      const groups = new Map();
      for (const t of trades) {
        const key = (t.alert_id && !SENTINEL_ALERT_IDS.has(String(t.alert_id)))
          ? `a:${t.alert_id}:${isCloseInstruction(t.instruction) ? 'c' : 'o'}`
          : `t:${t.id ?? `${t.created_at}|${t.symbol}|${t.instruction}`}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(t);
      }
      const all = [...groups.values()].map(grp => {
        grp.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const first = grp[0];
        // Net price across legs: sells are credit, buys are debit
        const netPrice = grp.reduce((s, t) =>
          s + (String(t.instruction || '').startsWith('SELL') ? 1 : -1) * (parseFloat(t.fill_price) || 0), 0);
        // Combined P/L% for grouped closing fills
        let pl = 0, basis = 0;
        for (const t of grp) {
          const mult = String(t.asset_type || '').toUpperCase() === 'EQUITY' ? 1 : 100;
          const proceeds = (parseFloat(t.fill_price) || 0) * (t.quantity || 0) * mult;
          const legPL = parseFloat(t.realized_pl) || 0;
          pl += legPL;
          basis += String(t.instruction || '').toUpperCase().startsWith('BUY') ? proceeds + legPL : proceeds - legPL;
        }
        const reasonPct = basis > 0 && pl !== 0 ? (pl / basis) * 100 : null;
        const isMulti = grp.length > 1;
        return {
        orderId: grp.map(t => t.id).join('-'),
        orderLegCollection: grp.map(t => ({
          instrument: { symbol: t.symbol, assetType: t.asset_type || 'OPTION' },
          instruction: t.instruction,
          quantity: t.quantity,
          price: t.fill_price,
        })),
        enteredTime: grp[grp.length - 1].created_at || '',
        orderType: isMulti ? (netPrice >= 0 ? 'NET_CREDIT' : 'NET_DEBIT') : (first.order_type || 'MARKET'),
        price: isMulti ? netPrice : first.fill_price,
        status: String(first.status || 'FILLED').toUpperCase(),
        reason: first.reason || null,
        reasonPct,
        _accountName: 'Paper',
        _accountId: 'paper',
        _broker: 'Paper',
        _accountType: 'paper',
        _paper: first,
        _paperLegs: grp,
        _source: paperSourceOf(first),
        };
      });
      setOrders(all);
    } catch (err) { console.error('Failed to fetch paper trades:', err); setOrders([]); }
    finally { setLoading(false); }
  }, []);

  const fetchOrders = useCallback(async (force = false) => {
    if (paperMode) { fetchPaperTrades(); return; }
    if (!accounts.length) return;
    setLoading(true);
    try {
      const list = selAcct === 'all' ? accounts : accounts.filter(a => String(a.id) === String(selAcct));
      const all = [];
      const stamps = [];
      for (const acct of list) {
        const aT = (acct.accountType || acct.apiType || '').toLowerCase();
        const isRhDirect = aT === 'robinhood' || aT === 'robinhood-api';
        const isRh = isRhDirect || aT === 'robinhood-mcp';
        const aN = acct.name || acct.nickname || acct.id || '';
        try {
          let r;
          if (isRhDirect) {
            r = await apiRequest(`/api/robinhood-api/order-history/${acct.id}${force ? '?refresh=1' : ''}`);
            normalizeRhHistory(r.data?.orders || [], aN, acct.id).forEach(o => all.push(o));
          } else if (isRh) {
            r = await apiRequest(`/api/mcp/robinhood/orders?accountId=${acct.id}${force ? '&refresh=1' : ''}`);
            (r.data?.orders || []).forEach(o => all.push(normalizeRhOrder(o, aN, acct.id)));
          } else {
            r = await apiRequest(`/api/schwab-api/orders?accountId=${acct.id}${force ? '&refresh=1' : ''}`);
            (r.data?.orders || []).forEach(o => all.push({
              ...o, enteredTime: o.enteredTime || o.enteredDate || '',
              status: String(o.status || '').toUpperCase(),
              _accountName: aN, _accountId: String(acct.id), _broker: 'Schwab',
              _accountType: 'schwab',
            }));
          }
          if (r?.data?.cachedAt) stamps.push(r.data.cachedAt);
        } catch (e) { console.warn(`Orders fetch failed for ${acct.id}:`, e.message); }
      }
      setOrders(all);
      if (stamps.length) setUpdatedAt(stamps.sort().pop());

      // Copy-trade provenance: map both directions so the copied order shows
      // "Copy" and the original shows "Copied →".
      try {
        const eR = await apiRequest('/api/copy-trading/events?limit=200');
        const evs = eR.data?.events || [];
        const byTarget = new Map(), bySource = new Map();
        for (const ev of evs) {
          if (ev.target_order_id) byTarget.set(String(ev.target_order_id), ev);
          bySource.set(String(ev.source_order_id), ev);
        }
        setCopyByTargetId(byTarget);
        setCopyBySourceId(bySource);
      } catch (e) { /* copy tables may not exist yet — indicator just stays off */ }
    } catch (err) { console.error('Failed to fetch orders:', err); setOrders([]); }
    finally { setLoading(false); }
    }, [accounts, selAcct, paperMode, fetchPaperTrades]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Background sync: the server keeps the order-history cache warm (fill
  // poller merges fills/cancels every minute, full sync every TTL), so a
  // periodic non-forced read is cheap and keeps this view current without
  // a manual refresh. Skips while the tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOrders();
    }, 60000);
    return () => clearInterval(id);
  }, [fetchOrders]);

  const handleSort = (f) => setSort(p => ({ field: f, dir: p.field === f && p.dir === 'asc' ? 'desc' : 'asc' }));

  const getSortVal = (o, f) => {
    const legs = o.orderLegCollection || [];
    const leg = legs[0] || {};
    const map = {
      enteredTime: o.enteredTime || o.enteredDate || '',
      status: String(o.status || '').toUpperCase(),
      symbol: (leg.instrument?.symbol || '').toLowerCase(),
      instruction: String(leg.instruction || '').toLowerCase(),
      quantity: Number(leg.quantity || 0),
      price: Number(o.price || 0),
      strategy: detectStrategyFromOrderLegs(legs).toLowerCase(),
      account: (o._accountName || '').toLowerCase(),
      reason: (o.reason || '').toLowerCase(),
      source: (o._source || '').toLowerCase(),
    };
    return map[f] ?? '';
  };

  const filtered = orders.filter(o => {
    if (symbolFilter.trim()) {
      const { include, exclude } = parseSymbolFilter(symbolFilter);
      const legSyms = (o.orderLegCollection || []).map(l => (l.instrument?.symbol || '').toUpperCase().replace(/\s+/g, ''));
      const und = underlyingOf(o).toUpperCase();
      const matches = (t) => { const q = t.toUpperCase(); return und.includes(q) || legSyms.some(s => s.includes(q)); };
      if (exclude.some(matches)) return false;
      if (include.length && !include.some(matches)) return false;
    }
    if (sourceFilter !== 'all' && (o._source || '') !== sourceFilter) return false;
    if (strategyFilter !== 'all' && detectStrategyFromOrderLegs(o.orderLegCollection || []) !== strategyFilter) return false;
    if (statusFilter === 'all') return true;
    if (statusFilter === 'working') return ['WORKING','PENDING_ACTIVATION','LIVE','PENDING','QUEUED','UNCONFIRMED','CONFIRMED','PARTIALLY_FILLED'].includes(o.status);
    if (statusFilter === 'filled') return o.status === 'FILLED';
    if (statusFilter === 'cancelled') return o.status === 'CANCELED' || o.status === 'CANCELLED';
    if (statusFilter === 'rejected') return o.status === 'REJECTED';
    if (statusFilter === 'expired') return o.status === 'EXPIRED';
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = getSortVal(a, sort.field), bv = getSortVal(b, sort.field);
    if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
    return sort.dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const SH = ({ field, label, align }) => (
    <TableCell align={align || 'left'}>
      <TableSortLabel active={sort.field === field} direction={sort.dir} onClick={() => handleSort(field)}>
        <strong>{label}</strong>
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Symbol"
            placeholder="e.g. SPXW"
            value={symbolFilter}
            onChange={e => setSymbolFilter(e.target.value)}
            sx={{ minWidth: 110, width: 140 }}
          />
          {!paperMode && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Account</InputLabel>
            <Select value={selAcct} label="Account" onChange={e => setSelAcct(e.target.value)}>
              <MenuItem value="all">All Accounts</MenuItem>
              {accounts.map(a => (
                <MenuItem key={a.id} value={String(a.id)}>{a.name || a.nickname || a.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="working">Working</MenuItem>
              <MenuItem value="filled">Filled</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Strategy</InputLabel>
            <Select value={strategyFilter} label="Strategy" onChange={e => setStrategyFilter(e.target.value)}>
              <MenuItem value="all">All Strategies</MenuItem>
              {[...new Set(orders.map(o => detectStrategyFromOrderLegs(o.orderLegCollection || [])))].sort().map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {paperMode && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={e => setSourceFilter(e.target.value)}>
              <MenuItem value="all">All Sources</MenuItem>
              {[...new Set(orders.map(o => o._source).filter(Boolean))].sort().map(s => (
                <MenuItem key={s} value={s}>{fmtSource(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          )}
          {!paperMode && updatedAt && (
            <Typography variant="caption" color="textSecondary">
              Updated {new Date(updatedAt).toLocaleString()}
            </Typography>
          )}
          <IconButton onClick={() => fetchOrders(true)} disabled={loading}><RefreshIcon /></IconButton>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : sorted.length === 0 ? (
        <Card><CardContent><Typography color="textSecondary">No orders found.</Typography></CardContent></Card>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <SH field="enteredTime" label="Time" />
                <SH field="account" label="Account" />
                <SH field="symbol" label="Symbol" />
                <SH field="strategy" label="Strategy" />
                <SH field="instruction" label="Action" />
                <SH field="quantity" label="Qty" align="right" />
                <SH field="price" label="Price" align="right" />
                <SH field="status" label="Status" />
                {paperMode && <SH field="reason" label="Reason" />}
                {paperMode && <SH field="source" label="Source" />}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((o, i) => {
                const legs = (o.orderLegCollection && o.orderLegCollection.length > 0) ? o.orderLegCollection : [{}];
                const isMultiLeg = legs.length > 1;
                const strategyType = detectStrategyFromOrderLegs(o.orderLegCollection || []);

                // Order-level cells shared by single-leg rows and multi-leg header rows.
                const statusCell = (() => {
                  // Copy provenance: prefer the events lookup, fall back to the
                  // GLA_COPY_<orderId> tag Schwab stores on the order.
                  const ev = copyByTargetId.get(String(o.orderId));
                  const tagSrc = String(o.tag || '').startsWith('GLA_COPY_') ? String(o.tag).slice(9) : null;
                  const srcId = ev ? ev.source_order_id : tagSrc;
                  const copiedFrom = ev?.source_account_id != null
                    ? accounts.find(a => String(a.id) === String(ev.source_account_id))?.name || `acct ${ev.source_account_id}`
                    : null;
                  const srcEv = copyBySourceId.get(String(o.orderId));
                  const copiedTo = srcEv?.target_account_id != null
                    ? accounts.find(a => String(a.id) === String(srcEv.target_account_id))?.name || `acct ${srcEv.target_account_id}`
                    : null;
                  return (
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        <Chip label={o.status} size="small" color={getStatusColor(o.status)} />
                        {srcId && (
                          <Tooltip title={`Copy-traded from order ${srcId}${copiedFrom ? ` on ${copiedFrom}` : ''}`} arrow>
                            <Chip label="Copy" size="small" color="secondary" variant="filled" />
                          </Tooltip>
                        )}
                        {srcEv && (
                          <Tooltip title={`Copied to ${copiedTo || `acct ${srcEv.target_account_id}`}${srcEv.target_order_id ? ` → order ${srcEv.target_order_id}` : ''} (${srcEv.status})`} arrow>
                            <Chip label="Copied" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                })();

                const reasonCell = (
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {o.reason && (() => {
                      const info = REASON_INFO[o.reason];
                      const pct = o.reasonPct != null ? ` ${o.reasonPct >= 0 ? '+' : ''}${o.reasonPct.toFixed(0)}%` : '';
                      return info ? (
                        <Tooltip title={info.title}>
                          <Chip label={`${info.label}${pct}`} size="small" color={info.color} variant="outlined" />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">{o.reason}</Typography>
                      );
                    })()}
                  </TableCell>
                );

                const sourceCell = (
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {o._source && <Chip label={fmtSource(o._source)} size="small" variant="outlined" />}
                  </TableCell>
                );

                const replayCell = (
                  <TableCell sx={{ width: 36, px: 0.25 }}>
                    <Tooltip title="Load this order into the trade screen to edit and re-submit">
                      <IconButton size="small" onClick={() => setEditOrder(orderToAlert(o))}>
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                );

                const priceNode = (px, orderType, instruction) => {
                  if (px == null || px === '') return null;
                  const cd = creditDebitTag(orderType, instruction);
                  return (
                    <>
                      {fc(px)}
                      {cd && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.4 }}
                          color={cd === 'Cr' ? 'success.main' : 'error.main'}>
                          {cd}
                        </Typography>
                      )}
                    </>
                  );
                };

                const orderQty = o.quantity ?? legs[0]?.quantity;

                const legSymbol = (leg) =>
                  leg.instrument?.assetType === 'OPTION' ? fmtOption(leg.instrument?.symbol || '') : (leg.instrument?.symbol || '');

                // Multi-leg: an order-level header row carries time/account/
                // strategy/net price; every leg renders beneath it with its own
                // instruction and leg price.
                if (isMultiLeg) {
                  return (
                    <React.Fragment key={`${o.orderId}-${i}`}>
                      <TableRow hover sx={{ borderTop: i > 0 ? '2px solid' : undefined, borderTopColor: 'divider' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(o.enteredTime)}</TableCell>
                        <TableCell sx={{ maxWidth: 160 }}><AccountWithLogo name={o._accountName} broker={o._broker} /></TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{underlyingOf(o)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{strategyType}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{getActionLabel(legs[0]?.instruction)}</TableCell>
                        <TableCell align="right">{orderQty || '-'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{priceNode(o.price, o.orderType, legs[0]?.instruction) || '-'}</TableCell>
                        {statusCell}
                        {paperMode && reasonCell}
                        {paperMode && sourceCell}
                        {replayCell}
                      </TableRow>
                      {legs.map((leg, legIdx) => (
                        <TableRow key={`${o.orderId}-${i}-leg-${legIdx}`} hover sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell /><TableCell />
                          <TableCell sx={{ fontSize: '0.75rem', pl: 3 }}>{legSymbol(leg)}</TableCell>
                          <TableCell />
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{fmtInstruction(leg.instruction) || '-'}</TableCell>
                          <TableCell align="right">{leg.quantity || '-'}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{priceNode(legFillPrice(o, leg, legIdx), null, leg.instruction)}</TableCell>
                          <TableCell />
                          {paperMode && <TableCell />}
                          {paperMode && <TableCell />}
                          <TableCell />
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                }

                const leg = legs[0];
                return (
                  <TableRow key={`${o.orderId}-${i}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(o.enteredTime)}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}><AccountWithLogo name={o._accountName} broker={o._broker} /></TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{legSymbol(leg)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{strategyType}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {leg.instruction ? `${getActionLabel(leg.instruction)} · ` : ''}{fmtInstruction(leg.instruction) || '-'}
                    </TableCell>
                    <TableCell align="right">{leg.quantity || orderQty || '-'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{priceNode(o.price, o.orderType, leg.instruction) || '-'}</TableCell>
                    {statusCell}
                    {paperMode && reasonCell}
                    {paperMode && sourceCell}
                    {replayCell}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reorder — loads the order into TradeDialog for editing before submit */}
      <TradeDialog open={!!editOrder} alert={editOrder} onClose={() => setEditOrder(null)} />
    </Box>
  );
};

export default OrderHistory;
